/** Fondos del lienzo: color + patrón (puntos, cuadrícula, líneas). */
import type { BackgroundId } from '../../shared/types.ts'
import type { Camera } from '../state/store.ts'

export interface BackgroundStyle {
  id: BackgroundId
  label: string
  fill: string
  pattern: 'none' | 'dots' | 'grid' | 'lines'
  patternColor: string
  /** el lienzo es oscuro: la interfaz sobre él usa contraste invertido */
  dark?: boolean
}

export const BACKGROUNDS: Record<BackgroundId, BackgroundStyle> = {
  plain: { id: 'plain', label: 'Blanco', fill: '#FFFFFF', pattern: 'none', patternColor: '#00000000' },
  dots: { id: 'dots', label: 'Puntos', fill: '#FFFFFF', pattern: 'dots', patternColor: '#C8C8C8' },
  grid: { id: 'grid', label: 'Cuadrícula', fill: '#FFFFFF', pattern: 'grid', patternColor: '#E4E4E4' },
  lines: { id: 'lines', label: 'Líneas', fill: '#FFFFFF', pattern: 'lines', patternColor: '#E4E4E4' },
  dark: { id: 'dark', label: 'Oscuro', fill: '#1F1F1F', pattern: 'dots', patternColor: '#3D3D3D', dark: true },
  blueprint: {
    id: 'blueprint',
    label: 'Plano',
    fill: '#0B3D66',
    pattern: 'grid',
    patternColor: '#2F6EA0',
    dark: true,
  },
}

export const GRID_SIZE = 40

/** Dibuja el fondo directamente en espacio de pantalla (más nítido que escalar el patrón). */
export function drawBackground(
  ctx: CanvasRenderingContext2D,
  style: BackgroundStyle,
  camera: Camera,
  width: number,
  height: number,
): void {
  ctx.fillStyle = style.fill
  ctx.fillRect(0, 0, width, height)
  if (style.pattern === 'none') return

  // Ajusta el paso de la cuadrícula al nivel de zoom para que nunca sature.
  let step = GRID_SIZE
  while (step * camera.z < 14) step *= 2
  while (step * camera.z > 140) step /= 2
  const spacing = step * camera.z
  if (spacing < 6) return

  const offsetX = -camera.x * camera.z
  const offsetY = -camera.y * camera.z
  const startX = offsetX % spacing
  const startY = offsetY % spacing

  ctx.save()
  if (style.pattern === 'dots') {
    const r = Math.min(1.6, Math.max(0.7, camera.z))
    ctx.fillStyle = style.patternColor
    for (let x = startX; x < width + spacing; x += spacing) {
      for (let y = startY; y < height + spacing; y += spacing) {
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  } else {
    ctx.strokeStyle = style.patternColor
    ctx.lineWidth = 1
    ctx.beginPath()
    if (style.pattern === 'grid') {
      for (let x = startX; x < width + spacing; x += spacing) {
        ctx.moveTo(Math.round(x) + 0.5, 0)
        ctx.lineTo(Math.round(x) + 0.5, height)
      }
    }
    for (let y = startY; y < height + spacing; y += spacing) {
      ctx.moveTo(0, Math.round(y) + 0.5)
      ctx.lineTo(width, Math.round(y) + 0.5)
    }
    ctx.stroke()
  }
  ctx.restore()
}
